import './Authors.css';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthors, searchAuthors } from '../../services/authorService';
import Pagination from '../Pagination/Pagination';

const AUTHORS_PER_PAGE = 12;

function Authors() {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [authors, setAuthors] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on new search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch authors when page or search term changes
    useEffect(() => {
        const loadAuthors = async () => {
            try {
                setLoading(true);
                setError(null);

                let pageData;
                if (debouncedSearchTerm.trim()) {
                    // Use search endpoint if there's a search term
                    pageData = await searchAuthors(debouncedSearchTerm.trim(), currentPage, AUTHORS_PER_PAGE);
                } else {
                    // Use regular fetch if no search term
                    pageData = await fetchAuthors(currentPage, AUTHORS_PER_PAGE);
                }

                setAuthors(pageData.content);
                setTotalPages(pageData.page.totalPages);
                setTotalElements(pageData.page.totalElements);
            } catch (err) {
                setError('Nepodařilo se načíst autory. Zkuste to prosím znovu.');
                console.error('Error loading authors:', err);
            } finally {
                setLoading(false);
            }
        };

        loadAuthors();
    }, [currentPage, debouncedSearchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleAuthorClick = (authorId) => {
        navigate(`/autori/${authorId}`);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className="authors-page">
            <div className="authors-container">
                <h1 className="page-title">Autoři</h1>

                <div className="search-bar-container">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Hledat autory podle jména"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <svg
                        className="search-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <p>Načítání autorů...</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p>{error}</p>
                    </div>
                ) : (
                    <>
                        <div className="authors-grid">
                            {authors.length > 0 ? (
                                authors.map(author => (
                                    <div
                                        key={author.id}
                                        className="author-tile"
                                        onClick={() => handleAuthorClick(author.id)}
                                    >
                                        <h2 className="author-name">{author.name}</h2>
                                        <p className="author-info">{author.shortInfo}</p>
                                    </div>
                                ))
                            ) : (
                                <div className="no-results">
                                    <p>Nebyli nalezeni žádní autoři odpovídající vašemu hledání.</p>
                                </div>
                            )}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            onPageChange={handlePageChange}
                            itemLabel="autorů"
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default Authors;
